import {useEffect, useLayoutEffect} from "react";
import {useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {TABLES} from "../../../../../Apis/Tables";
import {MODELS} from "../../../../../Apis/Models";
import {permissionObj} from "../../../../../Helpers/permission";
import {SUB_VIEW_MODEL} from "../../Model/Model";
import {useParamsContextProvider} from "../../../../../hooks/ParamsContextProvider";

/**
 * Table model each sub view of the requests tab is read with.
 */
const SUB_VIEW_TABLE_MODEL = {
    [SUB_VIEW_MODEL.REQUESTS.REQUEST]: TABLES.MATERIAL_CONTROL_ACTIVE,
    [SUB_VIEW_MODEL.REQUESTS.HISTORY]: TABLES.MATERIAL_CONTROL_HISTORY
};

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Handles the sub view of the requests tab.
 *
 * The requests in course and the history of the requests read different table
 * models, so the sub view decides which one the search is asked for.
 */
export const MaterialRequestSectionApp = ({
    subView,
    option,
    setSubView,
    setOption,
    setModuleId,
    setFinalArray,
    setSearchElement,
    setSearch,
    setCriteria
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    let {state} = location;
    const currentSubView = state?.currentSubView;
    // Context provider for tab navigation
    const {paramsContext} = useParamsContextProvider();

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);

    /**
     * Points the search at the sub view the tab is read with.
     *
     * The requests in course are the ones read first, and a sub view the tab
     * was left in is kept, so the tab is entered again where the user left it.
     * The table model is pointed every time the tab is entered, because the
     * tab it comes from left the search pointed at its own model and the
     * requests would be read with it.
     *
     * A sub view that already comes from the navigation is left alone, because
     * the tab restores it before this view is mounted and clears the state of
     * the history along with it.
     */
    useEffect(() => {
        if (!currentSubView && (DEV || permission?.includes(permissionObj?.WEB_MATERIAL_CONTROL_REQUEST_READ))) {
            changeSubView(subView || SUB_VIEW_MODEL.REQUESTS.REQUEST);
        }
    }, [paramsContext?.tabIndex]);

    /**
     * Restores the sub view that comes from the navigation state and then clears
     * the state from the history, so a reload does not restore it again.
     */
    useLayoutEffect(() => {
        if (currentSubView) {
            changeSubView(currentSubView);
            navigate(location.pathname, {replace: true, state: {}});
        }
    }, [location]);

    /**
     * Function for the requests button.
     */
    const handleBtnRequest = () => {
        changeSubView(SUB_VIEW_MODEL.REQUESTS.REQUEST);
    };

    /**
     * Function for the history button.
     */
    const handleBtnHistory = () => {
        changeSubView(SUB_VIEW_MODEL.REQUESTS.HISTORY);
    };

    /**
     * Moves to the given sub view and points the search to its table model.
     *
     * @param {string} subView - Sub view to read.
     */
    const changeSubView = (subView) => {
        const model = SUB_VIEW_TABLE_MODEL[subView];
        if (!model) return;

        // The requests are listed and exported by the module that reviews the
        // folios, so the file is generated with that module
        setModuleId(MODELS.MATERIAL_CONTROL_REQUEST);
        // The filters of one sub view are not read by the other one, so the
        // export of the sub view goes back to the simple endpoint along with them
        setFinalArray([]);
        setSearch(false);
        setCriteria(null);
        setSearchElement('');
        setSubView(subView);
        setOption(model);
    };

    /**
     * Tells whether the table of a sub view is already read with its own model.
     *
     * The table asks for its first page as soon as it is mounted, and a search
     * without a table model is answered with an error by the service, so the
     * table is mounted once the search is pointed at the model of its sub view.
     * A sub view that comes from the navigation is restored by the tab before
     * this view points the search, so the model is awaited for.
     *
     * @param {string} view - Sub view the table is read with.
     *
     * @returns {boolean} True when the table is read with the model of the sub view.
     */
    const isSubViewReady = (view) => subView === view && option === SUB_VIEW_TABLE_MODEL[view];

    return {
        handleBtnRequest,
        handleBtnHistory,
        isSubViewReady
    };
};
